import React, { useState, useEffect, useMemo } from 'react';
import Lottie from 'lottie-react';

const jsonPath = `${process.env.PUBLIC_URL || ''}/ecointel.json`;

/** Global scale vs design-time pixel props (e.g. 0.5 = half on-screen size). */
const DISPLAY_SCALE = 0.5;

const cache = { data: null, promise: null };

function loadLottieData() {
  if (cache.data) return Promise.resolve(cache.data);
  if (!cache.promise) {
    cache.promise = fetch(jsonPath)
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load ${jsonPath}: ${r.status}`);
        return r.json();
      })
      .then((json) => {
        cache.data = json;
        return json;
      })
      .catch((e) => {
        cache.promise = null;
        throw e;
      });
  }
  return cache.promise;
}

/**
 * EcoIntel branded Lottie loader. Uses `/ecointel.json` from `public/`.
 *
 * @param {string} [message] - Static caption when `messages` is not used.
 * @param {string[]} [messages] - If length >= 2, captions rotate every `cycleMs`.
 * @param {number} [cycleMs]
 * @param {boolean} [showMessage]
 * @param {number} [lottiePixelWidth] default aspect matches animation (160×200); scaled by DISPLAY_SCALE for display.
 * @param {number} [lottiePixelHeight]
 */
export function EcointelLottie({
  message = 'Loading...',
  messages = null,
  cycleMs = 4500,
  className = '',
  lottieClassName = '',
  showMessage = true,
  lottiePixelWidth = 120,
  lottiePixelHeight = 150,
}) {
  const w = Math.round(lottiePixelWidth * DISPLAY_SCALE);
  const h = Math.round(lottiePixelHeight * DISPLAY_SCALE);
  const [animationData, setAnimationData] = useState(cache.data);
  const [loadError, setLoadError] = useState(false);
  const list = useMemo(() => (Array.isArray(messages) && messages.length > 0 ? messages : null), [messages]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (animationData || loadError) return;
    let cancelled = false;
    loadLottieData()
      .then((d) => {
        if (!cancelled) setAnimationData(d);
      })
      .catch((e) => {
        console.error('EcointelLottie:', e);
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [animationData, loadError]);

  useEffect(() => {
    if (!list || list.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), cycleMs);
    return () => clearInterval(t);
  }, [list, cycleMs]);

  const displayMessage = list ? list[list.length === 1 ? 0 : idx] : message;

  if (loadError) {
    return (
      <div className={`flex flex-col items-center justify-center text-center max-w-sm ${className}`}>
        <p className="text-sm text-eddp-muted">{message}</p>
      </div>
    );
  }

  if (!animationData) {
    return (
      <div className={`flex flex-col items-center justify-center ${className}`} style={{ minHeight: h }}>
        <div
          className="rounded-md bg-transparent animate-pulse ring-1 ring-slate-300/25"
          style={{ width: w, height: h }}
          aria-hidden
        />
        {showMessage && <p className="text-sm text-slate-500 mt-3 px-2">{displayMessage}</p>}
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center justify-center text-center ${className}`}>
      <div
        className={`bg-transparent ${lottieClassName}`}
        style={{ width: w, height: h, backgroundColor: 'transparent' }}
      >
        <Lottie
          animationData={animationData}
          loop
          className="w-full h-full bg-transparent"
          style={{ background: 'transparent' }}
          aria-hidden
        />
      </div>
      {showMessage && (
        <p className="text-sm text-slate-600 mt-3 max-w-xs px-2 transition-opacity duration-300">{displayMessage}</p>
      )}
    </div>
  );
}

/** Centered loader for full-page or large regions */
export function EcoIntelFullBleed({ message, messages, cycleMs, className = '', minHeight = 'min-h-[50vh]' }) {
  return (
    <div className={`w-full flex items-center justify-center p-8 ${minHeight} ${className}`}>
      <EcointelLottie
        message={message}
        messages={messages}
        cycleMs={cycleMs}
        lottiePixelWidth={140}
        lottiePixelHeight={175}
      />
    </div>
  );
}

/**
 * Wrapper: children render underneath; when `show`, a frosted overlay with Lottie covers the box.
 * Parent should be `position: relative` (this adds `relative` to the wrapper root).
 */
export function EcoIntelLoadOverlay({
  show,
  children,
  message,
  messages,
  cycleMs,
  className = '',
  overlayClassName = '',
  backdropClassName = 'bg-white/88 backdrop-blur-[1px]',
  lottiePixelWidth = 110,
  lottiePixelHeight = 138,
}) {
  return (
    <div className={`relative ${className}`}>
      {children}
      {show ? (
        <div
          className={`absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] ${backdropClassName} ${overlayClassName}`}
          aria-busy="true"
          aria-live="polite"
        >
          <EcointelLottie
            message={message}
            messages={messages}
            cycleMs={cycleMs}
            lottiePixelWidth={lottiePixelWidth}
            lottiePixelHeight={lottiePixelHeight}
          />
        </div>
      ) : null}
    </div>
  );
}

/** Compact Lottie for buttons and list rows (no caption). */
export function EcointelLottieMark({ className = '', size = 20 }) {
  const h = Math.round(size * (200 / 160));
  return (
    <EcointelLottie
      showMessage={false}
      className={`shrink-0 ${className}`}
      lottiePixelWidth={size}
      lottiePixelHeight={h}
    />
  );
}
